<template>
  <div class="viewport viewport-with-top-nav viewport-compact">

    <TopNav></TopNav>

    <div class="display-1 py-3">{{ $t('LoanFullRepaymentPage.title') }}</div>
    <template>
      <Alert type="warning" v-if="!!calculations && !!calculations.currentChange">
        <div>{{
            $t('LoanFullRepaymentPage.pendingRepayment', {
              amount: $n(calculations.currentChange.totalAmountToBePaid, 'currencyNoDecimals'),
              date: $d(calculations.currentChange.waitingMoneySince)
            })
          }}
        </div>
      </Alert>
      <p v-else>{{ $t('LoanFullRepaymentPage.message1') }}</p>
      <div class="form-group mb-3" v-if="isNewCoreEnabled">
        <label for="calculationDate">{{ $t('LoanPaymentCalculatorPage.whenDoYouWantToPay') }}</label>
        <select class="custom-select" id="calculationDate" v-model="calculationDate" :disabled="calculations == null || canNotApplyForRepayment">
          <option :value="formattedISODate(getDate(index))" v-for="index in daysRange" :key="index">
            {{ $d(getDate(index), 'short') }}
          </option>
        </select>
      </div>
      <p>{{ $t('LoanFullRepaymentPage.message2', { date: $d(calculationDate) }) }}</p>
      <div v-if="!calculations">
        <div class="text-center">
          <spinner></spinner>
        </div>
      </div>
      <div v-else>
        <div>
          <table id="loan-calculations" class="table table-sm table-escape-container">
            <tbody>
            <tr v-if="hasInterest">
              <td class="small text-muted border-0">{{ $t('LoanFullRepaymentPage.interest') }}</td>
              <td class="small text-muted border-0 text-right">{{
                  $n(calculations.subtractedInterest, 'currency')
                }}
              </td>
            </tr>
            <tr v-if="hasSubtractedAdminFee">
              <td class="small text-muted">{{ $t('LoanFullRepaymentPage.adminFee') }}</td>
              <td class="small text-muted text-right">{{ $n(calculations.subtractedAdminFee, 'currency') }}</td>
            </tr>
            <tr v-if="hasSubtractedGuaranteeFee">
              <td class="small text-muted">{{ $t('LoanFullRepaymentPage.guaranteeFee') }}</td>
              <td class="small text-muted text-right">{{ $n(calculations.subtractedGuaranteeFee, 'currency') }}</td>
            </tr>
            <tr>
              <td v-if="calculations.savings > 0">{{ $t('LoanFullRepaymentPage.yourTotalSavings') }}</td>
              <td v-else>{{ $t('LoanFullRepaymentPage.youWillPayMore') }}</td>
              <td class="text-right" :class="{'text-success': calculations.savings > 0}">
                <strong>{{ $n(Math.abs(calculations.savings), 'currency') }}</strong>
              </td>
            </tr>
            <tr>
              <td>{{ $t('LoanFullRepaymentPage.newRemainingBalance') }}</td>
              <td class="text-right text-amount">{{ $n(calculations.toBePaidEarlier, 'currency') }}</td>
            </tr>
            </tbody>
          </table>
          <hr class="my-5 border-0"/>
        </div>
      </div>
      <div class="flex-grow-1 d-lg-none"></div>
      <div class="mb-4 py-3 px-4 small text-justify rounded" style="background-color: #f8f9fa"
           v-if="isNewCoreEnabled && !!calculations">
        <p>{{ $t('LoanFullRepaymentPage.disclaimerHeader') }}</p>
        <p class="mb-0">{{ $t('LoanFullRepaymentPage.disclaimerFooter') }}</p>
      </div>
      <button id="button-pay-now" class="btn btn-primary btn-block" @click="doEarlyRepayment()" v-if="!!calculations" :disabled="canNotApplyForRepayment">
        {{ $t('LoanFullRepaymentPage.payLoanNow') }}
      </button>
    </template>
    <div class="flex-grow-1 d-none d-lg-flex"></div>
  </div>
</template>

<script>
import TopNav from '@shared/components/TopNav'
import Alert from '@shared/components/Alert'
import addDays from 'date-fns/addDays'
import isBefore from 'date-fns/isBefore'
import formatISO from 'date-fns/formatISO'

export default {
  name: 'LoanFullRepaymentPage',
  components: { TopNav, Alert },
  data() {
    return {
      reference: this.$route.params.reference,
      loanEndDate: this.$route.params.loanEndDate,
      calculations: null,
      calculationDate: null,
      daysRange: 7
    }
  },
  async created() {
    this.calculationDate = this.formattedISODate(addDays(new Date(), (this.isNewCoreEnabled ? 1 : 0)))
    this.daysRange = this.calculateDaysRange(7)
    if (!this.isNewCoreEnabled) {
      await this.$http.post(`/api/loans/${this.reference}/repayment/early`)
    }
  },
  methods: {
    async doEarlyRepayment() {
      if (this.isNewCoreEnabled) {
        await this.$http.post(`/api/loans/${this.reference}/repayment/early`, null, { params: { date: this.calculationDate } })
      }
      await this.$router.push({
        name: 'external-payments',
        params: { id: this.reference },
        query: { amount: this.calculations.toBePaidEarlier }
      }).catch(() => {})
    },
    async calculate() {
      this.calculations = null
      const response = await this.$http.get(`/api/loans/${this.reference}/repayment/early?date=${this.calculationDate}`)
      this.calculations = response.data
    },
    getDate(shift) {
      return addDays(new Date(), shift)
    },
    formattedISODate(date) {
      return formatISO(date, { representation: 'date' })
    },
    calculateDaysRange(days) {
      if (days < 1) {
        return 0
      }
      return isBefore(addDays(new Date(), days), Date.parse(this.loanEndDate)) ? days : this.calculateDaysRange(days - 1)
    }
  },
  computed: {
    hasSubtractedAdminFee() {
      return 'subtractedAdminFee' in this.calculations && this.calculations.subtractedAdminFee > 0
    },
    hasSubtractedGuaranteeFee() {
      return 'subtractedGuaranteeFee' in this.calculations && this.calculations.subtractedGuaranteeFee > 0
    },
    hasInterest() {
      return 'subtractedInterest' in this.calculations && this.calculations.subtractedInterest > 0
    },
    isNewCoreEnabled() {
      return this.$configuration.newCoreEnabled
    },
    canNotApplyForRepayment() {
      return (this.$configuration.newCoreEnabled && this.calculations?.currentChange?.waitingMoneyUntil === this.calculationDate) || this.daysRange === 0
    }
  },
  watch: {
    calculationDate() {
      this.calculate()
    }
  }
}
</script>
